import { create, v, w } from '@dojo/framework/core/vdom';
import { icache } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { DNode } from '@dojo/framework/core/interfaces';
import { createNlsMiddleware, keymap, pathmap, userstate, lccstate, recommended } from '@holmescorp/lcc-state';
import { ParentTree, TopicAttrs } from '@holmescorp/lcc-state/interfaces';
import { RNSRow } from './rns-row';
import { isEmpty, find } from 'lodash-es';
import { Keys, convertToBoolean } from '@holmescorp/common';
import { status, TrackedStatePlus } from '../middleware/status';
import { publish } from '@holmescorp/pub-sub';
import { RecommendedAction } from './recommended-action';
import has from '@dojo/framework/core/has';

import themeCss from '../css/recommended-next-steps.m.css';

const factory = create({
	icache,
	nls: createNlsMiddleware('ditabook'),
	quizNls: createNlsMiddleware('quiz'),
	nlsSettings: createNlsMiddleware('settings'),
	theme,
	status,
	userstate,
	lccstate,
	recommended
});

export const RecommendedNextSteps = factory(
	({ middleware: { theme, nls, quizNls, nlsSettings, status, userstate, lccstate, recommended } }) => {
		const css = theme.classes(themeCss);
		const { root, header, action: actionClass, complete } = css;

		const hasAssessment = recommended.hasAssessment();
		const topic = recommended.topicsets(true);

		const {
			format,
			messages: {
				assess_button = '[ditabook] assess_button',
				practice_button = '[ditabook] practice_button',
				recommended_next_steps = '[ditabook] recommended_next_steps',
				domain: domain_header = '[ditabook] domain',
				functionalArea: fa_header = '[ditabook] functionalArea',
				reading: reading_header = '[ditabook] reading',
				quiz: quiz_header = '[ditabook] quiz',
				take_quiz = '[ditabook] take_quiz',
				retake_quiz = `[ditabook] retake_quiz`,
				get_started = `[ditabook] get_started`,
				take_pretest = '[ditabook] take_pretest',
				take_practice = '[ditabook] take_practice',
				dashboard_practice_type: practice_type = '[ditabook] practice_type',
				course_complete = '[ditabook] course_complete'
			}
		} = nls();

		const { dashboard_show_practice_exam = 'true' } = nlsSettings().messages;

		const { continue: continueText = '[quiz] continue' } = quizNls().messages;

		const { get, path } = userstate;
		const qp = get(path('quizPattern'));
		const get_tree = (practice_type: string): ParentTree => {
			const quizstate = get(path(practice_type));
			switch (practice_type) {
				case 'practice':
					return quizstate;

				case 'posttest':
					return quizstate.tree;
			}
			return {};
		};

		const practice_tree = get_tree(practice_type);

		const has_scores = (tree: ParentTree) => {
			const roots: string[] = lccstate.get(lccstate.path('ditaroots'));
			return roots.some((k) => tree[k].data.percent > 0);
		};

		const showPE = convertToBoolean(dashboard_show_practice_exam);
		const hasPractice = has_scores(practice_tree);

		const kids = topic.children
			.filter((val) => 'topicset' in val)
			.map((val) => {
				const { attributes = {} as TopicAttrs, children = [] } = val.topicset;
				const { title, key } = attributes;
				const [, , rootpath] = keymap.get(key)!.path.split('/');
				const data = keymap.get(rootpath);
				const roottitle = data ? data.title : '';

				return { title, rootTitle: roottitle, key, children };
			});

		const topics = kids.slice(0, 3).map((kid) => {
			const state = status.data(kid.key) ? status.data(kid.key) : ({} as TrackedStatePlus);

			let action: DNode | null = null;
			let score: DNode | string = '...';
			let progress: string = '...';

			if (!isEmpty(state)) {
				const { complete: statusComplete, quizFail, quizStarted, quizScore, reading, readingComplete } = state;
				score = quizScore;
				progress = reading;
				let complete = statusComplete;

				const topicgroups = kid.children.filter((k: any) => 'topicgroup' in k);

				// Calculate total differently if using Rise.
				const useRise = has('use-rise');
				if (useRise) {
					const total = topicgroups.length;
					let riseComplete = 0;
					topicgroups.map((k: any) => {
						const { key } = k.topicgroup.attributes;
						const state = status.data(key) ? status.data(key) : ({} as TrackedStatePlus);
						if (!isEmpty(state) && state.complete == state.total) {
							riseComplete++;
						}
					});
					complete = riseComplete;
					progress = complete > 0 ? format('complete_of_total', { complete, total }) : '...';
				}

				const readNext = find(topicgroups, (k: any) => {
					const { key } = k.topicgroup.attributes;
					const state = status.data(key) ? status.data(key) : ({} as TrackedStatePlus);
					if (!isEmpty(state)) {
						return state.complete !== state.total;
					}
					return true;
				});

				const retakeQuiz = quizStarted && quizFail && readingComplete;
				const takeQuiz = !quizStarted && readingComplete;

				const take = v(
					'span',
					{
						tabIndex: 0,
						classes: [actionClass],
						onclick: (event: MouseEvent) => {
							event && event.stopPropagation();
							const { path } = keymap.get(kid.key)!;
							const { view } = pathmap.get(`${path}/quiz`)!;
							view();
						},
						onkeydown: (event: KeyboardEvent) => {
							const { path } = keymap.get(kid.key)!;
							const { view } = pathmap.get(`${path}/quiz`)!;
							switch (event.key) {
								case Keys.Enter:
								case Keys.Space:
								case Keys.Spacebar:
									event.preventDefault();
									view();
							}
						}
					},
					[takeQuiz ? take_quiz : retake_quiz]
				);

				const cont = v(
					'span',
					{
						tabIndex: 0,
						classes: [actionClass],
						onclick: (event: MouseEvent) => {
							event && event.stopPropagation();
							const { key } = readNext.topicgroup.attributes;
							const { path } = keymap.get(key)!;
							const { view } = pathmap.get(path)!;
							view();
						},
						onkeydown: (event: KeyboardEvent) => {
							const { key } = readNext.topicgroup.attributes;
							const { path } = keymap.get(key)!;
							const { view } = pathmap.get(path)!;
							switch (event.key) {
								case Keys.Enter:
								case Keys.Space:
								case Keys.Spacebar:
									event.preventDefault();
									view();
							}
						}
					},
					[continueText]
				);

				const getStarted = v(
					'span',
					{
						tabIndex: 0,
						classes: [actionClass],
						onclick: (event: MouseEvent) => {
							event && event.stopPropagation();
							const { key } = readNext.topicgroup.attributes;
							const { path } = keymap.get(key)!;
							const { view } = pathmap.get(path)!;
							view();
						},
						onkeydown: (event: KeyboardEvent) => {
							const { key } = readNext.topicgroup.attributes;
							const { path } = keymap.get(key)!;
							const { view } = pathmap.get(path)!;
							switch (event.key) {
								case Keys.Enter:
								case Keys.Space:
								case Keys.Spacebar:
									event.preventDefault();
									view();
							}
						}
					},
					[get_started]
				);

				action = takeQuiz ? take : retakeQuiz ? take : !quizStarted && complete === 0 ? getStarted : cont;
			}

			return w(RNSRow, {
				domain: kid.rootTitle,
				functionalArea: kid.title,
				reading: progress,
				quiz: score,
				action: action
			});
		});

		const pretestWidget = w(RecommendedAction, {
			title: take_pretest,
			buttonLabel: assess_button,
			onclick: () => {
				publish('site/assessment');
			},
			onkeydown: (event: KeyboardEvent) => {
				switch (event.key) {
					case Keys.Enter:
					case Keys.Space:
					case Keys.Spacebar:
						event.preventDefault();
						publish('site/assessment');
				}
			}
		});

		const practiceWidget = w(RecommendedAction, {
			title: take_practice,
			buttonLabel: practice_button,
			onclick: () => {
				publish(`site/${practice_type}`);
			},
			onkeydown: (event: KeyboardEvent) => {
				switch (event.key) {
					case Keys.Enter:
					case Keys.Space:
					case Keys.Spacebar:
						event.preventDefault();
						publish(`site/${practice_type}`);
				}
			}
		});

		return v(
			'div',
			{
				id: `dashboard-recommended-next-steps-${qp}`,
				xqa: `dashboard-recommended-next-steps-${qp}`,
				role: 'table',
				classes: [theme.variant(), root]
			},
			[
				v('h1', { classes: [header] }, [recommended_next_steps]),
				!hasAssessment
					? pretestWidget
					: kids.length > 0
					? v('div', { classes: [themeCss.recommendedList] }, [
							w(RNSRow, {
								domain: domain_header,
								functionalArea: fa_header,
								reading: reading_header,
								quiz: quiz_header,
								action: null,
								header: true
							}),
							...topics
					  ])
					: showPE && !hasPractice
					? practiceWidget
					: v('div', { classes: [complete] }, [course_complete])
			]
		);
	}
);

export default RecommendedNextSteps;
